import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import { API_BASE_URL } from '../../config';
import TemplateEditor from '../../components/admin/behavior/TemplateEditor';
import TemplatePreviewCard from '../../components/admin/behavior/TemplatePreviewCard';
import TemplateDiffModal from '../../components/admin/behavior/TemplateDiffModal';
import ApprovalQueueTable from '../../components/admin/behavior/ApprovalQueueTable';
import AuditLogTable from '../../components/admin/behavior/AuditLogTable';
import { readableTemplateLabel } from '../../components/admin/behavior/templateKeyPresentation';
import PanelHelpBadge from '../../components/admin/PanelHelpBadge';
import DashboardModuleTabs, { DASHBOARD_WORKSPACE_HIDDEN_PATHS } from '../../components/admin/dashboard/DashboardModuleTabs';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const EMPTY_EDITOR = {
  id: null,
  templateKey: '',
  i18nKey: '',
  segment: 'FRICTION',
  variant: 'primary',
  tone: 'neutral',
  text: '',
  changeSummary: '',
  notesForReviewer: ''
};

const STATUS_LABELS = {
  draft: 'Taslak',
  in_review: 'İncelemede',
  approved: 'Onaylandı',
  active: 'Yayında',
  rejected: 'Reddedildi',
  archived: 'Arşivde'
};

const SEGMENT_LABELS = {
  RESET: 'Sıfırlama',
  FRICTION: 'Sürtünme',
  STABLE: 'Dengeli',
  STRONG: 'Güçlü'
};

const VARIANT_LABELS = {
  primary: 'Birincil',
  secondary: 'İkincil',
  reassurance: 'Destekleyici'
};

const ACTION_LABELS = {
  submit: 'İncelemeye gönderme',
  approve: 'Onaylama',
  reject: 'Reddetme',
  activate: 'Yayına alma',
  archive: 'Arşivleme'
};

const FILTER_WINDOWS = [
  { value: 7, label: 'Son 7 Gün' },
  { value: 30, label: 'Son 30 Gün' },
  { value: 90, label: 'Son 90 Gün' }
];

function statusLabel(status) {
  return STATUS_LABELS[String(status || '').toLowerCase()] || String(status || '-');
}

function segmentLabel(segment) {
  return SEGMENT_LABELS[String(segment || '').toUpperCase()] || String(segment || '-');
}

function variantLabel(variant) {
  return VARIANT_LABELS[String(variant || '').toLowerCase()] || String(variant || '-');
}

function formatDateTr(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch (_err) {
    return String(value);
  }
}

function withinDays(value, days) {
  if (!value) return false;
  const ts = new Date(value).getTime();
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts <= (days * 24 * 60 * 60 * 1000);
}

function getStatusCount(rows, key) {
  if (key === 'active') {
    return rows.filter((t) => Boolean(t.is_active) || String(t.status || '').toLowerCase() === 'active').length;
  }
  return rows.filter((t) => String(t.status || '').toLowerCase() === key).length;
}

function toPercent(value, total) {
  const n = Number(value || 0);
  const t = Math.max(1, Number(total || 0));
  return Math.round((n / t) * 100);
}

function exportAsJson(payload, name) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function exportAsCsv(lines, name) {
  const csvContent = `\uFEFF${lines.join('\r\n')}`;
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function BehaviorTemplatesPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editor, setEditor] = useState(EMPTY_EDITOR);
  const [validation, setValidation] = useState(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [showRawData, setShowRawData] = useState(false);

  const [segmentFilter, setSegmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [windowDays, setWindowDays] = useState(30);
  const [surfaceFilter, setSurfaceFilter] = useState('Tüm Yüzeyler');
  const [contextFilter, setContextFilter] = useState('Tüm Bağlamlar');
  const [compareEnabled, setCompareEnabled] = useState(false);

  async function fetchTemplates() {
    setLoading(true);
    setError('');
    try {
      const headers = getAdminHeaders();
      const [tplRes, logRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/templates`, { credentials: 'include', headers }),
        fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/templates/audit?limit=50`, { credentials: 'include', headers })
      ]);

      const tplJson = tplRes.ok ? await tplRes.json() : null;
      const logJson = logRes.ok ? await logRes.json() : null;
      if (!tplRes.ok) throw new Error(tplJson?.error || 'templates_fetch_failed');

      setTemplates(Array.isArray(tplJson?.templates) ? tplJson.templates : []);
      setAuditLogs(Array.isArray(logJson?.logs) ? logJson.logs : []);
    } catch (err) {
      setError(err?.message || 'Veriler alınamadı');
    } finally {
      setLoading(false);
    }
  }

  async function refreshPreview(nextEditor) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getAdminHeaders()
      };
      const payload = { ...nextEditor };
      const res = await fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/templates/validate-preview`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload)
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'validate_preview_failed');
      setValidation(json.validation || null);
      setPreview(String(json.renderedPreview || ''));
    } catch (_err) {
      setValidation(null);
      setPreview('');
    }
  }

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    refreshPreview(editor);
  }, [editor.templateKey, editor.i18nKey, editor.segment, editor.variant, editor.tone, editor.text]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((row) => {
      if (!withinDays(row.updated_at, windowDays)) return false;
      if (segmentFilter !== 'all' && String(row.segment || '').toUpperCase() !== segmentFilter) return false;
      if (statusFilter !== 'all' && String(row.status || '').toLowerCase() !== statusFilter) return false;
      return true;
    });
  }, [templates, windowDays, segmentFilter, statusFilter]);

  const activeTemplates = useMemo(
    () => filteredTemplates.filter((t) => Boolean(t.is_active) || String(t.status) === 'active'),
    [filteredTemplates]
  );

  const draftTemplates = useMemo(
    () => filteredTemplates.filter((t) => ['draft', 'rejected'].includes(String(t.status))),
    [filteredTemplates]
  );

  const inReviewTemplates = useMemo(
    () => filteredTemplates.filter((t) => String(t.status) === 'in_review' || String(t.status) === 'approved'),
    [filteredTemplates]
  );

  const summaryCounts = useMemo(() => {
    const total = filteredTemplates.length;
    const draft = getStatusCount(filteredTemplates, 'draft');
    const inReview = getStatusCount(filteredTemplates, 'in_review');
    const approved = getStatusCount(filteredTemplates, 'approved');
    const active = getStatusCount(filteredTemplates, 'active');
    return { total, draft, inReview, approved, active };
  }, [filteredTemplates]);

  const funnelLargestDrop = useMemo(() => {
    const a = summaryCounts.draft;
    const b = summaryCounts.inReview;
    const c = summaryCounts.approved;
    const d = summaryCounts.active;
    const drops = [
      { label: 'Taslak → İnceleme', value: a - b },
      { label: 'İnceleme → Onay', value: b - c },
      { label: 'Onay → Yayın', value: c - d }
    ].sort((x, y) => y.value - x.value);
    return drops[0];
  }, [summaryCounts]);

  const variantBreakdown = useMemo(() => {
    const map = new Map();
    filteredTemplates.forEach((row) => {
      const key = String(row.variant || 'primary').toLowerCase();
      const prev = map.get(key) || { total: 0, active: 0 };
      prev.total += 1;
      if (String(row.status || '').toLowerCase() === 'active' || row.is_active) prev.active += 1;
      map.set(key, prev);
    });
    return ['primary', 'secondary', 'reassurance'].map((key) => {
      const item = map.get(key) || { total: 0, active: 0 };
      return {
        key,
        label: variantLabel(key),
        total: item.total,
        activeRate: toPercent(item.active, item.total || 1)
      };
    });
  }, [filteredTemplates]);

  const decisionHighlights = useMemo(() => {
    const total = Math.max(1, summaryCounts.total);
    const publishRate = toPercent(summaryCounts.active, total);
    const bestVariant = [...variantBreakdown].sort((a, b) => b.activeRate - a.activeRate)[0] || { label: '-', activeRate: 0 };

    return {
      strongest: {
        title: 'En Güçlü Sinyal',
        text: `${bestVariant.label} mesajları daha iyi yayın dönüşümü gösteriyor`,
        metric: `+%${bestVariant.activeRate}`
      },
      problem: {
        title: 'En Büyük Problem',
        text: funnelLargestDrop?.label
          ? `${funnelLargestDrop.label} geçişinde birikme yüksek`
          : 'Geçiş verisi henüz oluşmadı',
        metric: funnelLargestDrop?.value > 0 ? `-%${toPercent(funnelLargestDrop.value, summaryCounts.total)}` : '%0'
      },
      impact: {
        title: 'Yayın Etkisi',
        text: 'Yayına alınan şablon oranı hedefin altında kalıyor olabilir',
        metric: `%${publishRate}`
      }
    };
  }, [summaryCounts, variantBreakdown, funnelLargestDrop]);

  const anaCikarim = useMemo(() => {
    if ((funnelLargestDrop?.value || 0) > 0) {
      return `Ana çıkarım: En kritik kırılım "${funnelLargestDrop.label}" adımında. Öncelik bu geçişi iyileştirmek olmalı.`;
    }
    if (summaryCounts.active > 0) {
      return 'Ana çıkarım: Yayın hattı stabil görünüyor. Odak, yayın oranını kademeli artırmak olmalı.';
    }
    return 'Ana çıkarım: Karar için yeterli sinyal oluşması adına önce şablon akışını doldurmak gerekiyor.';
  }, [funnelLargestDrop, summaryCounts.active]);

  const rootCauses = useMemo(() => {
    const causes = [];
    if (summaryCounts.draft > summaryCounts.active) {
      causes.push({ title: 'Taslak birikimi yüksek', confidence: 'Orta güven' });
    }
    if (summaryCounts.inReview > summaryCounts.approved) {
      causes.push({ title: 'İnceleme kuyruğu yavaş ilerliyor', confidence: 'Yüksek güven' });
    }
    if ((funnelLargestDrop?.value || 0) > 0) {
      causes.push({ title: `${funnelLargestDrop.label} adımında kayıp`, confidence: 'Orta güven' });
    }
    if (causes.length < 3) {
      causes.push({ title: 'Sürüm açıklamaları eksik/dağınık olabilir', confidence: 'Düşük güven' });
    }
    return causes.slice(0, 3);
  }, [summaryCounts, funnelLargestDrop]);

  const topBestTemplates = useMemo(() => {
    return activeTemplates
      .slice()
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0))
      .slice(0, 10);
  }, [activeTemplates]);

  const topWeakTemplates = useMemo(() => {
    return filteredTemplates
      .filter((row) => ['rejected', 'draft'].includes(String(row.status || '').toLowerCase()))
      .sort((a, b) => new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime())
      .slice(0, 10);
  }, [filteredTemplates]);

  function fillEditor(row) {
    setEditor({
      id: row.id,
      templateKey: row.template_key || '',
      i18nKey: row.i18n_key || '',
      segment: row.segment || 'FRICTION',
      variant: row.variant || 'primary',
      tone: row.tone || 'neutral',
      text: row.text || '',
      changeSummary: row.change_summary || '',
      notesForReviewer: row.notes_for_reviewer || ''
    });
    setSuccess('');
    setError('');
    setShowRawData(true);
  }

  async function saveDraft() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getAdminHeaders()
      };
      const body = {
        templateKey: editor.templateKey,
        i18nKey: editor.i18nKey,
        segment: editor.segment,
        variant: editor.variant,
        tone: editor.tone,
        text: editor.text,
        changeSummary: editor.changeSummary,
        notesForReviewer: editor.notesForReviewer
      };

      const isUpdate = Boolean(editor.id);
      const url = isUpdate
        ? `${API_BASE_URL}/api/admin/kitchen/behavior/templates/${editor.id}`
        : `${API_BASE_URL}/api/admin/kitchen/behavior/templates/draft`;
      const method = isUpdate ? 'PATCH' : 'POST';

      const res = await fetch(url, { method, credentials: 'include', headers, body: JSON.stringify(body) });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'draft_save_failed');

      setSuccess(isUpdate ? 'Taslak güncellendi' : 'Taslak oluşturuldu');
      setEditor((prev) => ({ ...prev, id: json?.template?.id || prev.id }));
      await fetchTemplates();
    } catch (err) {
      setError(err?.message || 'Taslak kaydı başarısız');
    } finally {
      setSaving(false);
    }
  }

  async function runAction(id, action, extraBody = null) {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getAdminHeaders()
      };
      const res = await fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/templates/${id}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify(extraBody || {})
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || `${action}_failed`);
      setSuccess(`${ACTION_LABELS[action] || 'İşlem'} başarılı`);
      await fetchTemplates();
    } catch (err) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  }

  async function openDiff(row) {
    const version = Number(row?.version || 0);
    if (version <= 1) return;
    try {
      const headers = getAdminHeaders();
      const res = await fetch(`${API_BASE_URL}/api/admin/kitchen/behavior/templates/${row.id}/diff?against=${version - 1}`, {
        credentials: 'include',
        headers
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'diff_fetch_failed');
      setDiffData(json?.diff || null);
      setDiffOpen(true);
    } catch (err) {
      setError(err?.message || 'Fark verisi yüklenemedi');
    }
  }

  function exportJson() {
    const payload = {
      context: {
        segment: segmentFilter === 'all' ? 'Tüm Segmentler' : segmentLabel(segmentFilter),
        window: `Son ${windowDays} Gün`,
        surface: surfaceFilter,
        context: contextFilter,
        compare: compareEnabled
      },
      templates: filteredTemplates,
      audit: auditLogs
    };
    exportAsJson(payload, `davranis-modul-sablonlari-${Date.now()}.json`);
  }

  function exportCsv() {
    const lines = [
      ['Senaryo', 'Mesaj Tipi', 'Durum', 'Sürüm', 'Teknik Anahtar', 'Güncelleme'].join(',')
    ];
    filteredTemplates.forEach((row) => {
      const label = readableTemplateLabel(row.template_key, row.version).title;
      const [scenario = '', variant = ''] = label.split('·').map((s) => s.trim());
      const cols = [
        scenario,
        variant,
        statusLabel(row.status),
        `v${Number(row.version || 0)}`,
        row.template_key || '',
        formatDateTr(row.updated_at)
      ].map((col) => `"${String(col).replace(/"/g, '""')}"`);
      lines.push(cols.join(','));
    });

    exportAsCsv(lines, `davranis-modul-sablonlari-${Date.now()}.csv`);
  }

  const contextSummary = `Segment: ${segmentFilter === 'all' ? 'Tüm Segmentler' : segmentLabel(segmentFilter)} • Son ${windowDays} Gün • ${surfaceFilter} • ${contextFilter}${compareEnabled ? ' • Karşılaştırma Açık' : ''}`;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0d141c]">
      <AdminSidebar
        mobileOpen={mobileSidebarOpen}
        onCloseMobile={() => setMobileSidebarOpen(false)}
        hiddenPaths={DASHBOARD_WORKSPACE_HIDDEN_PATHS}
      />
      <main className="flex-1 md:ml-[var(--admin-sidebar-width,18rem)] flex flex-col min-h-screen transition-[margin] duration-300">
        <AdminTopBar title="Davranış Modül Şablonları" onToggleMobile={() => setMobileSidebarOpen(true)} />

        <div className="p-4 sm:p-8 flex-1 space-y-6">
          <DashboardModuleTabs />

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4 space-y-4">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black text-slate-900 dark:text-white">Davranış Modül Şablonları</h1>
              <PanelHelpBadge
                title="Davranış Modül Şablonları"
                description="Bu ekran, şablon yönetimini karar odaklı katmanlarla sunar."
                interpretation="Önce ne oluyor, sonra nerede oluyor, sonra neden ve ne denemeli akışı izlenir."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <select value={segmentFilter} onChange={(e) => setSegmentFilter(e.target.value)} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                <option value="all">Tüm Segmentler</option>
                {Object.entries(SEGMENT_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                {FILTER_WINDOWS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </select>
              <select value={surfaceFilter} onChange={(e) => setSurfaceFilter(e.target.value)} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                <option>Tüm Yüzeyler</option>
                <option>Mobil</option>
                <option>Masaüstü</option>
              </select>
              <select value={contextFilter} onChange={(e) => setContextFilter(e.target.value)} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm">
                <option>Tüm Bağlamlar</option>
                <option>Yüksek Sürtünme</option>
                <option>Karar Desteği</option>
                <option>Güven Mesajı</option>
              </select>
              <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                <input type="checkbox" checked={compareEnabled} onChange={(e) => setCompareEnabled(e.target.checked)} />
                Karşılaştır
              </label>
            </div>

            <p className="text-sm text-slate-500">{contextSummary}</p>
            {error ? <p className="text-sm font-bold text-rose-600">{error}</p> : null}
            {success ? <p className="text-sm font-bold text-emerald-600">{success}</p> : null}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">{decisionHighlights.strongest.title}</p>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{decisionHighlights.strongest.text}</p>
              <p className="mt-3 text-2xl font-black text-emerald-600">{decisionHighlights.strongest.metric}</p>
            </article>
            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">{decisionHighlights.problem.title}</p>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{decisionHighlights.problem.text}</p>
              <p className="mt-3 text-2xl font-black text-rose-500">{decisionHighlights.problem.metric}</p>
            </article>
            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <p className="text-xs uppercase text-slate-500">{decisionHighlights.impact.title}</p>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">{decisionHighlights.impact.text}</p>
              <p className="mt-3 text-2xl font-black text-cyan-500">{decisionHighlights.impact.metric}</p>
            </article>
          </section>

          <section className="rounded-2xl border border-cyan-200/70 bg-cyan-50/70 p-4 dark:border-cyan-800 dark:bg-cyan-950/30">
            <p className="text-sm font-semibold text-cyan-900 dark:text-cyan-100">{anaCikarim}</p>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><p className="text-xs text-slate-500">Taslak</p><p className="text-2xl font-black">{summaryCounts.draft}</p></div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><p className="text-xs text-slate-500">İnceleme</p><p className="text-2xl font-black">{summaryCounts.inReview}</p></div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><p className="text-xs text-slate-500">Onay</p><p className="text-2xl font-black">{summaryCounts.approved}</p></div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"><p className="text-xs text-slate-500">Yayın</p><p className="text-2xl font-black">{summaryCounts.active}</p></div>
            </div>

            <div>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Taslak → İnceleme → Onay → Yayın</p>
              <div className="mt-2 grid grid-cols-4 gap-2 text-xs">
                {[summaryCounts.draft, summaryCounts.inReview, summaryCounts.approved, summaryCounts.active].map((count, idx) => (
                  <div key={idx} className="rounded bg-slate-100 dark:bg-slate-800 px-2 py-1 text-center">{count}</div>
                ))}
              </div>
              <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">En büyük düşüş: {funnelLargestDrop?.label || '-'} ({funnelLargestDrop?.value || 0})</p>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Mesaj Tipi Performansı</h3>
              <div className="mt-3 space-y-2">
                {variantBreakdown.map((row) => (
                  <div key={row.key} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                    <span>{row.label}</span>
                    <span className="font-semibold">%{row.activeRate} yayın</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">Özet: Birincil ve destekleyici mesaj dengesini koruyup inceleme birikimini azaltmak gerekir.</p>
            </article>

            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">En Olası Nedenler</h3>
              <div className="mt-3 space-y-2">
                {rootCauses.map((cause, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm flex items-center justify-between">
                    <span>{idx + 1}. {cause.title}</span>
                    <span className="text-xs text-slate-500">{cause.confidence}</span>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Önerilen Testler</h3>
            <div className="mt-3 space-y-2 text-sm">
              <label className="flex items-center gap-2"><input type="checkbox" /> Birincil mesajları daha kısa ve net hale getir</label>
              <label className="flex items-center gap-2"><input type="checkbox" /> İnceleme bekleyen şablonlarda günlük onay kotası uygula</label>
              <label className="flex items-center gap-2"><input type="checkbox" /> Destekleyici mesajları sürtünme segmentinde artır</label>
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">En İyi Çalışanlar</h3>
              <div className="mt-3 space-y-2">
                {topBestTemplates.length === 0 ? <p className="text-sm text-slate-500">Veri yok.</p> : topBestTemplates.map((row) => (
                  <div key={row.id} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                    <div>
                      <p className="font-semibold">{readableTemplateLabel(row.template_key, row.version).title}</p>
                      <p className="text-xs text-slate-500">{statusLabel(row.status)}</p>
                    </div>
                    <button type="button" className="text-primary font-semibold" onClick={() => fillEditor(row)}>Detay</button>
                  </div>
                ))}
              </div>
            </article>

            <article className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">En Zayıf Çalışanlar</h3>
              <div className="mt-3 space-y-2">
                {topWeakTemplates.length === 0 ? <p className="text-sm text-slate-500">Veri yok.</p> : topWeakTemplates.map((row) => (
                  <div key={row.id} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm">
                    <div>
                      <p className="font-semibold">{readableTemplateLabel(row.template_key, row.version).title}</p>
                      <p className="text-xs text-rose-500">Sorun etiketi: {statusLabel(row.status)}</p>
                    </div>
                    <button type="button" className="text-primary font-semibold" onClick={() => fillEditor(row)}>Detay</button>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#151f2b] p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowRawData((prev) => !prev)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-bold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {showRawData ? 'Tüm Veriyi Gizle' : 'Tüm Veriyi Göster'}
              </button>
              <div className="flex gap-2">
                <button type="button" onClick={exportCsv} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold">CSV Dışa Aktar</button>
                <button type="button" onClick={exportJson} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold">JSON Dışa Aktar</button>
              </div>
            </div>

            {showRawData ? (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 pt-2">
                <section className="xl:col-span-2 space-y-4">
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="text-base font-bold">Ham Şablon Tablosu</h3>
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-slate-500">
                            <th className="py-2 pr-3">Şablon</th>
                            <th className="py-2 pr-3">Segment</th>
                            <th className="py-2 pr-3">Durum</th>
                            <th className="py-2 pr-3">Sürüm</th>
                            <th className="py-2 pr-3">Güncelleme</th>
                            <th className="py-2 pr-3">İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTemplates.map((row) => (
                            <tr key={row.id} className="border-t border-slate-100 dark:border-slate-800">
                              <td className="py-2 pr-3">
                                <p className="font-semibold">{readableTemplateLabel(row.template_key, row.version).title}</p>
                                <p className="text-xs text-slate-500">{readableTemplateLabel(row.template_key, row.version).technicalKey}</p>
                              </td>
                              <td className="py-2 pr-3">{segmentLabel(row.segment)}</td>
                              <td className="py-2 pr-3">{statusLabel(row.status)}</td>
                              <td className="py-2 pr-3">v{row.version}</td>
                              <td className="py-2 pr-3">{formatDateTr(row.updated_at)}</td>
                              <td className="py-2 pr-3">
                                <button className="text-primary font-bold" onClick={() => fillEditor(row)}>Düzenle</button>
                                {Number(row.version) > 1 ? (
                                  <button className="ml-2 text-slate-500 font-bold" onClick={() => openDiff(row)}>Fark</button>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="text-base font-bold">Onay Kuyruğu</h3>
                    <div className="mt-3">
                      <ApprovalQueueTable
                        rows={inReviewTemplates}
                        onApprove={(id) => runAction(id, 'approve')}
                        onReject={(id) => runAction(id, 'reject', { reason: 'manual_reject' })}
                        onActivate={(id) => runAction(id, 'activate')}
                        onArchive={(id) => runAction(id, 'archive')}
                      />
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <TemplateEditor
                    value={editor}
                    onChange={setEditor}
                    onSave={saveDraft}
                    onReset={() => setEditor(EMPTY_EDITOR)}
                    saving={saving}
                  />
                  <TemplatePreviewCard preview={preview} validation={validation} />
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4">
                    <h3 className="text-base font-bold">Denetim Kaydı</h3>
                    <div className="mt-3">
                      <AuditLogTable rows={auditLogs} />
                    </div>
                  </div>
                </section>
              </div>
            ) : null}
          </section>

          {loading ? (
            <section className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922] p-4 text-sm text-slate-500 dark:text-slate-400">
              Davranış modül verileri yükleniyor...
            </section>
          ) : null}
        </div>
      </main>

      <TemplateDiffModal open={diffOpen} onClose={() => setDiffOpen(false)} diff={diffData} />
    </div>
  );
}
